"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createConversionParameters = createConversionParameters;
exports.createOnQuantityChange = createOnQuantityChange;
exports.convertQuantity = convertQuantity;
exports.getConversionParameters = getConversionParameters;
exports.getQuantitiesToConvertTo = getQuantitiesToConvertTo;
exports.isConvertableQuantity = isConvertableQuantity;
exports.getQuantityDefault = getQuantityDefault;
const property_1 = require("@genesys/property");
const ts_exhaustive_check_1 = require("ts-exhaustive-check");
const uom_1 = require("@genesys/uom");
const relative_humidity_conversions_1 = require("./conversions/relative-humidity-conversions");
const humidity_ratio_conversions_1 = require("./conversions/humidity-ratio-conversions");
const specific_enthalpy_conversions_1 = require("./conversions/specific-enthalpy-conversions");
const dew_point_temperature_conversions_1 = require("./conversions/dew-point-temperature-conversions");
const wet_temperature_conversions_1 = require("./conversions/wet-temperature-conversions");
const flow_conversions_1 = require("./conversions/flow-conversions");
const quantity_conversion_registry_1 = require("./quantity-conversion-registry");
function createConversionParameters(quantityConversionParams, selectedProperties) {
    if (quantityConversionParams.length === 0) {
        return undefined;
    }
    const params = {
        density: getAmountOfTypeOrUndefined(selectedProperties, quantityConversionParams, "Density"),
        pressure: getAmountOfTypeOrUndefined(selectedProperties, quantityConversionParams, "Pressure"),
        temperature: getAmountOfTypeOrUndefined(selectedProperties, quantityConversionParams, "Temperature"),
        humidityRatio: getAmountOfTypeOrUndefined(selectedProperties, quantityConversionParams, "HumidityRatio")
    };
    return params.density !== undefined ||
        params.pressure !== undefined ||
        params.temperature !== undefined
        ? params
        : undefined;
}
function createOnQuantityChange(onPropertyFormatChanged, measureSystem, quantityDefaults, propertyName) {
    return (quantity) => {
        // Take the default unit. The user have to change to the desired unit in the new quantity.
        const quantityDefault = getQuantityDefault(quantityDefaults, measureSystem, quantity);
        onPropertyFormatChanged(propertyName, quantityDefault.unit, quantityDefault.decimalCount);
    };
}
function convertQuantity(fromAmount, to, conversionParameters) {
    const requiredConversionParameters = getConversionParameters(fromAmount.unit.quantity, to, conversionParameters);
    switch (fromAmount.unit.quantity) {
        case "HumidityRatio": {
            return (0, humidity_ratio_conversions_1.humidityRatioConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "RelativeHumidity": {
            return (0, relative_humidity_conversions_1.relativeHumidityConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "SpecificEnthalpy": {
            return (0, specific_enthalpy_conversions_1.specificEnthalpyConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "DewPointTemperature": {
            return (0, dew_point_temperature_conversions_1.dewPointTemperatureConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "WetTemperature": {
            return (0, wet_temperature_conversions_1.wetTemperatureConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "MassFlow": {
            return (0, flow_conversions_1.massFlowConversions)(fromAmount, to, requiredConversionParameters);
        }
        case "VolumeFlow": {
            return (0, flow_conversions_1.volumeFlowConversions)(fromAmount, to, requiredConversionParameters);
        }
        default: {
            throw new Error(`Can't convert from quantity ${fromAmount}`);
        }
    }
}
function getConversionParameters(fromQuantity, toQuantity, conversionParameters) {
    const conversionType = getConversionTypeFromQuantity(fromQuantity, true);
    const densityAmount = conversionParameters.density;
    const pressureAmount = conversionParameters.pressure;
    const temperatureAmount = conversionParameters.temperature;
    const humidityRatioAmount = conversionParameters.humidityRatio;
    switch (conversionType) {
        case "Flow": {
            if (densityAmount === undefined &&
                (humidityRatioAmount === undefined ||
                    pressureAmount === undefined ||
                    temperatureAmount === undefined)) {
                throw new Error("Missing required amounts for flow conversion");
            }
            return {
                density: densityAmount,
                humidityRatio: humidityRatioAmount,
                pressure: pressureAmount,
                temperature: temperatureAmount
            };
        }
        case "Humidity": {
            if (pressureAmount === undefined) {
                throw new Error("Missing pressure amount");
            }
            if ((fromQuantity === "HumidityRatio" &&
                toQuantity === "DewPointTemperature") ||
                (fromQuantity === "DewPointTemperature" &&
                    toQuantity === "HumidityRatio")) {
                return { pressure: pressureAmount };
            }
            if (temperatureAmount === undefined) {
                throw new Error("Missing temperature amount");
            }
            return { pressure: pressureAmount, temperature: temperatureAmount };
        }
        default: {
            (0, ts_exhaustive_check_1.exhaustiveCheck)(conversionType);
            return {};
        }
    }
}
function getQuantitiesToConvertTo(quantity) {
    const conversionType = getConversionTypeFromQuantity(quantity, true);
    const quantities = quantity_conversion_registry_1.quantityFromAndToRegistry.get(conversionType);
    if (!quantities) {
        throw new Error(`Can't convert from ${quantity}`);
    }
    return quantities;
}
function isConvertableQuantity(quantity) {
    return !!getConversionTypeFromQuantity(quantity, false);
}
function getConversionTypeFromQuantity(quantity, throwOnMissing) {
    switch (quantity) {
        case "MassFlow":
        case "VolumeFlow": {
            return "Flow";
        }
        case "HumidityRatio":
        case "RelativeHumidity":
        case "WetTemperature":
        case "SpecificEnthalpy":
        case "DewPointTemperature": {
            return "Humidity";
        }
        default: {
            if (throwOnMissing) {
                throw new Error(`No ConversionType found for quantity ${quantity}`);
            }
            else {
                return undefined;
            }
        }
    }
}
function getQuantityDefault(quantityDefaults, measureSystem, quantity) {
    try {
        const quantityDefault = quantityDefaults[quantity][measureSystem];
        const unit = quantityDefault.unit;
        const decimalCount = quantityDefault.decimalCount;
        return {
            unit,
            decimalCount
        };
    }
    catch (e) {
        const msg = `Couldn't get unit from quantity: ${quantity}. Falling back to promaster default`;
        console.warn(msg);
        const quantityUnits = uom_1.Format.getUnitsForQuantity(quantity);
        if (quantityUnits.length === 0) {
            throw new Error("Couldn't find any units for quantity: " + quantityUnits);
        }
        const unit = quantityUnits[0];
        const unitFormat = uom_1.Format.getUnitFormat(unit, uom_1.UnitsFormat);
        const decimalCount = unitFormat !== undefined ? unitFormat.decimalCount : 2;
        return {
            unit,
            decimalCount
        };
    }
}
function getAmountOfTypeOrUndefined(pvs, conversionParameters, quantity) {
    const conversionAmountProperties = conversionParameters.map(cp => property_1.PropertyValueSet.getAmount(cp, pvs));
    const amount = conversionAmountProperties.find(a => !!(a && a.unit.quantity === quantity));
    if (!amount || amount.unit.quantity !== quantity) {
        return undefined;
    }
    return amount;
}
//# sourceMappingURL=functions.js.map